// Shared zod primitives — reused across every DTO in this package so the
// email/password/pagination rules are declared exactly once and can't drift
// between the login schema and the register schema, or between api and web.
import { z } from 'zod';

/** Normalises to lowercase before validating — email is the unique key on
 * `User` (spec §3.3), so case must not create duplicate accounts. */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, 'Email is required')
  .email('Enter a valid email address')
  .max(254);

/** Argon2id hashing (§10) makes length the only cost-relevant limit here —
 * complexity is enforced by requiring at least one letter and one digit,
 * matching the `SEED_ADMIN_PASSWORD` bootstrap floor
 * (`apps/api/src/config/env.schema.ts`, min 8) plus a reasonable ceiling to
 * bound hashing cost / prevent DoS via pathologically long inputs. */
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must be at most 128 characters')
  .regex(/[A-Za-z]/, 'Password must contain at least one letter')
  .regex(/[0-9]/, 'Password must contain at least one number');

export const nameSchema = z.string().trim().min(1, 'Name is required').max(120);

/** Opaque bearer token embedded in verification/invite/reset links
 * (`VerificationToken`-style row, hashed at rest) — not a JWT, just a
 * high-entropy random string, so only bounded as a non-empty string. */
export const opaqueTokenSchema = z.string().trim().min(16).max(512);

/** Cursor pagination envelope (spec §6 "Cursor pagination
 * (`?cursor=&limit=`, max 100)"). */
export const paginationQuerySchema = z.object({
  cursor: z.string().trim().min(1).optional(),
  limit: z.coerce.number().int().positive().max(100).default(20),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export function paginatedResponseSchema<ItemSchema extends z.ZodTypeAny>(item: ItemSchema) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
  });
}

// ─── Authoring primitives (P1-02, spec §3.3/§3.5/§6.2) ─────────────────────
// Shared across ExamBoard/Level/Subject/Section/ResourceTab/Resource so the
// slug/code/title/position rules can't drift between entities that all carry
// the same shape (spec §3.5: "Ordering is explicit ... maintained as a dense
// integer sequence by the bulk `reorder` endpoints" — `position` is NEVER a
// client-supplied field on create; it's assigned server-side on append and
// only ever mutated via the `reorder`/`move` payloads below).

/** A Prisma `cuid()` id referenced from a request body/path (`examBoardId`,
 * `parentId`, ...) — bounded as a non-empty string rather than validated
 * against the exact cuid alphabet, so this stays forward-compatible with any
 * future id scheme without a DTO change. */
export const idSchema = z.string().trim().min(1, 'id is required');

/** `slug` — "derived from name, editable, stable for URLs" (spec §3.5) on
 * `ExamBoard`/`Level`/`Subject`/`Section`. Lowercase kebab-case; a client MAY
 * omit it on create and let the service derive one from `name`/`title`. */
export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, 'Slug is required')
  .max(160)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be lowercase kebab-case (e.g. "a-level")');

/** `ExamBoard.code` / `Level.code` — "free text ... the system has no
 * opinion" (spec §3.5, "No enums"). Only bounded for storage/display
 * sanity, never restricted to a fixed value set. */
export const codeSchema = z.string().trim().min(1, 'Code is required').max(60);

/** Display name shared by every authoring entity (`ExamBoard.name`,
 * `Level.name`, `Subject.name`, `Section.title`, ...). */
export const titleSchema = z.string().trim().min(1, 'Title is required').max(200);

/** Optional free-text description/summary field, shared by
 * `ExamBoard`/`Level`/`Section`. */
export const descriptionSchema = z.string().trim().max(2000).optional();

/** Dense, zero-based integer position — only ever appears in the `reorder`/
 * `move` payloads below; never accepted on a plain create/update body (spec
 * §3.5, see this section's header comment). */
export const positionSchema = z.number().int().nonnegative();

/** One entry of a bulk `reorder` payload (spec §6.2: `POST .../reorder
 * [{ id, position }]` — one transaction, dense integer sequence). */
export const reorderItemSchema = z.object({
  id: idSchema,
  position: positionSchema,
});
export type ReorderItem = z.infer<typeof reorderItemSchema>;

/** L2 fix (P1-BUG-5 authoring security hardening; tightened again in the
 * security-engineer's pre-merge review — see REQUIRED-2, then a further
 * post-approval security re-review that found REQUIRED-2 still didn't
 * close): a per-request ceiling on how many `{ id, position }` entries a
 * single bulk `reorder` call may carry. Without this, an unbounded array
 * lets a caller submit a reorder large enough to exceed D1's documented
 * ~100-bound-parameter-per-query ceiling. The reorder path has TWO
 * consumers of this array, and the tighter one governs:
 * `apps/api/src/services/sections.ts`'s `buildOptimisticGuardStatement`
 * binds `2N + 2` params in ONE statement (the real-world binding pressure
 * point), while the plain `id IN (...)` existence check
 * (`sections.ts:777`) binds `N` and only becomes a problem past 100 items.
 * 500 was the original value ("well under D1's own limit" — actually ~5x
 * OVER it); 50 was the first fix attempt but `2*50+2 = 102` is ITSELF over
 * the 100-bound-param ceiling. The invariant that must hold is
 * `2 * MAX_REORDER_ITEMS + 2 <= 100`, i.e. `MAX_REORDER_ITEMS <= 49`. 40
 * keeps comfortable headroom (`2*40+2 = 82`) while staying in the same
 * order of magnitude as real sibling-group sizes this app's authoring tree
 * plausibly produces (spec §3.3 `MAX_SECTION_DEPTH` keeps trees shallow, so
 * genuine reorders are a handful of items, never dozens) — this is a
 * defensive ceiling against a malicious/misbehaving caller, not a
 * realistic in-product limit. Do not raise this without re-deriving both
 * consumers' bound-param math against D1's current documented limit and
 * re-checking `2 * MAX_REORDER_ITEMS + 2 <= 100`. */
export const MAX_REORDER_ITEMS = 40;

/** The bulk `reorder` request body itself — a bare JSON array, per spec
 * §6.2's literal payload shape `[{ id, position }]` (not wrapped in an
 * envelope object). Shared verbatim by every entity's `.../reorder` route
 * (`exam-boards`, `levels`, `subjects`, `sections`, `resources`) — each
 * entity file re-exports it under an entity-specific name so callers/OpenAPI
 * docs get a meaningful schema name without duplicating the shape. */
export const reorderRequestSchema = z
  .array(reorderItemSchema)
  .min(1, 'At least one item required')
  .max(MAX_REORDER_ITEMS, `At most ${MAX_REORDER_ITEMS} items may be reordered in one request`);
export type ReorderRequest = z.infer<typeof reorderRequestSchema>;
